'use client';

import { Link, useLocation } from 'react-router';
import {
  type ComponentProps,
  type ReactNode,
  type Ref,
  useTransition,
} from 'react';
import { cn } from '@/lib/utils';

type ActiveLinkRenderProps = { isActive: boolean; isPending: boolean };

type ActiveLinkProps = Omit<
  ComponentProps<typeof Link>,
  'to' | 'className' | 'children'
> & {
  href: string;
  end?: boolean;
  className?: string | ((props: ActiveLinkRenderProps) => string);
  children?: ReactNode | ((props: ActiveLinkRenderProps) => ReactNode);
};

function ActiveLinkBody({
  end,
  href,
  pathname,
  className,
  children,
  isPending,
}: {
  end?: boolean;
  href: string;
  pathname: string;
  className?: string | ((props: ActiveLinkRenderProps) => string);
  children?: ReactNode | ((props: ActiveLinkRenderProps) => ReactNode);
  isPending: boolean;
}) {
  const isActive = end
    ? pathname === href
    : pathname === href || pathname.startsWith(`${href}/`);
  const renderProps: ActiveLinkRenderProps = { isActive, isPending };
  const resolvedClassName =
    typeof className === 'function' ? className(renderProps) : className;
  const resolvedChildren =
    typeof children === 'function' ? children(renderProps) : children;

  return (
    <span
      className={cn(
        'flex min-w-0 w-full items-center gap-2',
        resolvedClassName,
        isPending && 'transition-opacity duration-150',
      )}
      aria-busy={isPending || undefined}
      data-link-pending={isPending ? '' : undefined}
    >
      {resolvedChildren}
    </span>
  );
}

export function ActiveLink({ href, className, children, end, ref, ...props }: ActiveLinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const location = useLocation();
  const pathname = location.pathname;
  const [isPending] = useTransition();

  return (
    <Link
      to={href}
      ref={ref}
      className="contents"
      {...props}
    >
      <ActiveLinkBody
        end={end}
        href={href}
        pathname={pathname}
        className={className}
        isPending={isPending}
      >
        {children}
      </ActiveLinkBody>
    </Link>
  );
}
